import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  Typography,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { FC, useState, useEffect } from 'react';
import { useJobseekerAddExperienceMutation, useJobseekerUpdateExperienceMutation } from '@carcareers/store';
import { useAuth } from '@carcareers/hooks';
import { JobseekerExperience } from '@libs/models';

interface JobseekerExperienceModalProps {
  type: 'add' | 'edit';
  open: boolean;
  onClose: () => void;
  experience?: JobseekerExperience;
}

export const JobseekerExperienceModal: FC<JobseekerExperienceModalProps> = ({ type, open, onClose, experience }) => {
  const { session } = useAuth();
  const [addExperience, { isLoading: isAdding }] = useJobseekerAddExperienceMutation();
  const [updateExperience, { isLoading: isUpdating }] = useJobseekerUpdateExperienceMutation();

  const [formData, setFormData] = useState({
    title: '',
    company: '',
    description: '',
    current: false,
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    if (type === 'edit' && experience) {
      setFormData({
        title: experience.title || '',
        company: experience.company || '',
        description: experience.description || '',
        current: experience.current || false,
        startDate: experience.startDate ? new Date(experience.startDate).toISOString().split('T')[0] : '',
        endDate: experience.endDate ? new Date(experience.endDate).toISOString().split('T')[0] : '',
      });
    } else if (type === 'add') {
      setFormData({
        title: '',
        company: '',
        description: '',
        current: false,
        startDate: '',
        endDate: '',
      });
    }
  }, [type, experience, open]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!session?.profile?.id) return;

    try {
      if (type === 'add') {
        await addExperience({
          jobseekerId: session.profile.id,
          title: formData.title,
          company: formData.company,
          description: formData.description,
          current: formData.current,
          startDate: new Date(formData.startDate),
          endDate: formData.current || !formData.endDate ? undefined : new Date(formData.endDate),
        }).unwrap();
      } else if (type === 'edit' && experience) {
        await updateExperience({
          id: Number(experience.id),
          jobseekerId: Number(session.profile.id),
          title: formData.title,
          company: formData.company,
          description: formData.description,
          current: formData.current,
          startDate: new Date(formData.startDate),
          endDate: formData.current || !formData.endDate ? undefined : new Date(formData.endDate),
        }).unwrap();
      }

      if (type === 'add') {
        setFormData({
          title: '',
          company: '',
          description: '',
          current: false,
          startDate: '',
          endDate: '',
        });
      }
      onClose();
    } catch (error) {
      console.error(`Error ${type === 'add' ? 'adding' : 'updating'} experience:`, error);
    }
  };

  const isFormValid = formData.title && formData.company && formData.description && formData.startDate;
  const isLoading = isAdding || isUpdating;

  const isDateValid = formData.current || !formData.endDate || !formData.startDate || new Date(formData.endDate) > new Date(formData.startDate);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 4,
          },
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">{type === 'add' ? 'Add Experience' : 'Edit Experience'}</Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <TextField
            label="Job Title"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            fullWidth
            required
            sx={{ borderRadius: 4 }}
            InputProps={{
              sx: { borderRadius: 4 },
            }}
          />
          <TextField
            label="Company"
            value={formData.company}
            onChange={(e) => handleInputChange('company', e.target.value)}
            fullWidth
            required
            sx={{ borderRadius: 4 }}
            InputProps={{
              sx: { borderRadius: 4 },
            }}
          />
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            fullWidth
            multiline
            rows={4}
            required
            sx={{ borderRadius: 4 }}
            InputProps={{
              sx: { borderRadius: 4 },
            }}
          />
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <TextField
              label="Start Date"
              type="date"
              value={formData.startDate}
              onChange={(e) => handleInputChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              slotProps={{
                htmlInput: {
                  shrink: true,
                },
                input: {
                  sx: { borderRadius: 4 },
                },
              }}
              fullWidth
              required
              sx={{ borderRadius: 4 }}
            />

            {!formData.current && (
              <TextField
                label="End Date"
                type="date"
                value={formData.endDate}
                onChange={(e) => handleInputChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                slotProps={{
                  htmlInput: {
                    shrink: true,
                  },
                  input: {
                    sx: { borderRadius: 4 },
                  },
                }}
                fullWidth
                sx={{ borderRadius: 4 }}
                error={!isDateValid && !!formData.endDate && !!formData.startDate}
                helperText={!isDateValid && !!formData.endDate && !!formData.startDate ? 'End date must be after start date' : ''}
              />
            )}
          </Box>
          <FormControlLabel
            control={
              <Checkbox checked={formData.current} onChange={(e) => handleInputChange('current', e.target.checked)} />
            }
            label="I currently work here"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid || !isDateValid || isLoading}
          sx={{ borderRadius: 2 }}
        >
          {isLoading
            ? type === 'add'
              ? 'Adding...'
              : 'Updating...'
            : type === 'add'
            ? 'Add Experience'
            : 'Update Experience'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
